import axiosInstance from '../config/axios';

const getAuthHeader = (extraHeaders = {}) => {
  const token = localStorage.getItem('authToken');
  return {
    ...extraHeaders,
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

// ──────────────────────────────────────────────
// Community APIs
// ──────────────────────────────────────────────

/**
 * Fetch all communities with optional filters
 * @param {{ search?: string, type?: string, category?: string }} params
 */
export const getCommunitiesApi = (params = {}) =>
  axiosInstance.get('/get-community', { params, headers: getAuthHeader() }).then((res) => res.data);

/**
 * Fetch a single community by ID
 * @param {string} id
 */
export const getCommunityByIdApi = (id) =>
  axiosInstance.get(`/community/${id}`, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Fetch all categories
 */
export const getCategoriesApi = () =>
  axiosInstance.get('/categories', { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Create a new community
 * @param {FormData} formData 
 */
export const createCommunityApi = (formData) =>
  axiosInstance.post('/add-community', formData, {
    headers: getAuthHeader({ 'Content-Type': 'multipart/form-data' })
  }).then((res) => res.data);


/**
 * Update an existing community
 * @param {string} id 
 * @param {FormData} formData 
 */
export const updateCommunityApi = (id, formData) =>
  axiosInstance.put(`/edit-community/${id}`, formData, {
    headers: getAuthHeader({ 'Content-Type': 'multipart/form-data' })
  }).then((res) => res.data);

/**
 * Delete a community
 * @param {string} id 
 */
export const deleteCommunityApi = (id) =>
  axiosInstance.delete(`/delete-community/${id}`, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Join a community
 * @param {string} id 
 * @param {string} userId 
 */
export const joinCommunityApi = (id, userId) =>
  axiosInstance.patch(`/community/${id}/join/${userId}`, {}, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Approve a join request (for owners)
 * @param {string} id 
 * @param {string} userId 
 */
export const approveRequestApi = (id, userId) =>
  axiosInstance.patch(`/community/${id}/requests/${userId}/approve`, {}, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * List community members
 * @param {string} id 
 * @param {string} status 'approved' or 'pending'
 */
export const getCommunityMembersApi = (id, status = 'approved') =>
  axiosInstance.get(`/community/${id}/members`, { params: { status }, headers: getAuthHeader() }).then((res) => res.data);

/**
 * Search communities
 * @param {{ search?: string, type?: string, category?: string }} params 
 */
export const searchCommunitiesApi = (params = {}) =>
  axiosInstance.get('/search-community', { params, headers: getAuthHeader() }).then((res) => res.data);

/**
 * Remove a member from a community
 * @param {string} communityId 
 * @param {string} userId 
 */
export const removeMembershipApi = (communityId, userId) =>
  axiosInstance.delete(`/communities/${communityId}/members/${userId}`, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * List pending join requests
 * @param {string} communityId 
 */
export const getPendingRequestsApi = (communityId) =>
  axiosInstance.get(`/communities/${communityId}/pending-members`, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Reject a join request
 * @param {string} communityId 
 * @param {string} userId 
 */
export const rejectRequestApi = (communityId, userId) =>
  axiosInstance.delete(`/communities/${communityId}/members/${userId}/reject`, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Search for members within a community
 * @param {{ communityId: string, search: string }} params 
 */
export const searchCommunityMembersApi = (params) =>
  axiosInstance.get('/search-community-member', { params, headers: getAuthHeader() }).then((res) => res.data);

/**
 * List posts within a community
 * @param {string} communityId 
 * @param {object} params Pagination/filters
 */
export const getCommunityPostsApi = (communityId, params = {}) =>
  axiosInstance.get(`/community/${communityId}/posts`, { params, headers: getAuthHeader() }).then((res) => res.data);

/**
 * Create a post within a community
 * @param {string} communityId 
 * @param {FormData} formData 
 */
export const createCommunityPostApi = (communityId, formData) =>
  axiosInstance.post(`/community/${communityId}/posts`, formData, {
    headers: getAuthHeader({ 'Content-Type': 'multipart/form-data' })
  }).then((res) => res.data);

/**
 * Fetch workbooks for a community
 * @param {string} communityId 
 */
export const getCommunityWorkbooksApi = (communityId) =>
  axiosInstance.get(`/community-workbook/${communityId}`, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * List videos for a community
 * @param {string} communityId 
 */
export const getCommunityVideosApi = (communityId) =>
  axiosInstance.get('/list-community-videos', { params: { communityId }, headers: getAuthHeader() }).then((res) => res.data);

/**
 * Toggle community live status
 * @param {string} id 
 * @param {object} data Object containing isLive status
 */
export const communityLiveApi = (id, data) =>
  axiosInstance.patch(`/communities/${id}/live`, data, { headers: getAuthHeader() }).then((res) => res.data);
